import { useCallback, useMemo, useRef, useState } from "react";
import { useCardDetailsErrorsStore } from "./useCardDetailsErrorsStore";
import { useTranslation } from "@/hooks/useTranslation";
import { useDonationFormStore } from "@/stores";
import { useValidationControl } from "@/hooks/useValidationControl";
import type {
  HostedFields,
  BraintreeError,
  HostedFieldsEvent,
} from "braintree-web";
import { useBraintree } from "./useBraintree";
import { trackError } from "@/utils/error-monitoring";
import { getCountryIso2 } from "@/utils/countries";

type PaymentErrors = "number" | "expirationDate" | "cvv";
type ValidateResponse = {
  number?: string | null;
  expirationDate?: string | null;
  cvv?: string | null;
} | null;

const errorTypes = ["CUSTOMER", "MERCHANT", "NETWORK", "INTERNAL", "UNKNOWN"];

// The 3DS lookup or Cardinal never answered: a connection problem, not a
// defect. THREEDS_CARDINAL_SDK_ERROR stays an error until its details tell
// what it covers.
const THREEDS_FAILURE_LEVELS: Record<string, "warning" | "error"> = {
  THREEDS_LOOKUP_ERROR: "warning",
  THREEDS_CARDINAL_SDK_RESPONSE_TIMEDOUT: "warning",
  THREEDS_CARDINAL_SDK_ERROR: "error",
};

// CUSTOMER errors are the gateway declining what the donor typed (bad card
// data, failed 3DS). They're the expected outcome of a wrong card, not a
// defect, so they get a field message but never reach Sentry.
function trackUnlessCustomerError(error: BraintreeError) {
  if (error.type !== "CUSTOMER") {
    trackError(error as Error);
  }
}

// Map Braintree's invalid field keys onto the donor-facing messages, dropping any
// key we have no message for. Extracted from validate(): the reduce sat four levels
// deep inside try/switch/case, which is most of that function's cognitive load.
function invalidFieldErrors(
  fields: PaymentErrors[],
  errorMessages: Record<PaymentErrors, string | null>,
): Record<PaymentErrors, string | null> {
  return fields.reduce(
    (acc, field) => {
      const message = errorMessages[field];

      if (message) {
        acc[field] = message;
      }

      return acc;
    },
    {} as Record<PaymentErrors, string | null>,
  );
}

export function isBraintreeError(e: unknown): e is BraintreeError {
  const error = e as BraintreeError;

  if (!error?.type) return false;
  return errorTypes.includes(error.type) && error instanceof Error;
}

export function useCardDetailsValidation(amount: number) {
  const { enabled } = useValidationControl();

  const t = useTranslation("card_details");

  const setErrors = useCardDetailsErrorsStore((state) => state.setErrors);
  const resetErrors = useCardDetailsErrorsStore((state) => state.resetErrors);
  const setIsValidated = useCardDetailsErrorsStore(
    (state) => state.setIsValidated,
  );
  const [validFields, setValidFields] = useState<
    Record<PaymentErrors, boolean>
  >({
    number: false,
    expirationDate: false,
    cvv: false,
  });

  const { setGatewayPayload, address } = useDonationFormStore();

  const isTokenizingRef = useRef(false);

  const errorMessages = useMemo(() => {
    return {
      number: t("errors.card-number") as string,
      expirationDate: t("errors.expiration-date") as string,
      cvv: t("errors.cvv") as string,
    };
  }, [t]);

  const { threeDSecure } = useBraintree();

  const validate = useCallback(
    async (instance?: HostedFields): Promise<ValidateResponse> => {
      setIsValidated(true);
      if (!enabled) {
        return null;
      }

      if (!instance) {
        setErrors(errorMessages);
        return errorMessages;
      }

      // Braintree rejects a tokenize() started while another is still in
      // flight. Returning an empty error object stops the duplicate attempt
      // without showing the donor a message: the validation already running
      // is the one that decides the outcome.
      if (isTokenizingRef.current) {
        return {};
      }
      isTokenizingRef.current = true;

      try {
        const { nonce, details } = await instance.tokenize();

        if (!threeDSecure) {
          setGatewayPayload({ nonce });
          resetErrors();
          return null;
        }

        const verifyResult = await threeDSecure.verifyCard({
          nonce,
          amount: `${amount}`,
          bin: details.bin,
          billingAddress: {
            streetAddress: address["address-line-1"],
            locality: address.city,
            region: address.region,
            postalCode: address["postal-code"],
            countryCodeAlpha2: getCountryIso2(address.country),
          },
        });

        if (!verifyResult.threeDSecureInfo?.liabilityShifted) {
          const errors = {
            number: t("errors.invalid-card") as string,
          };
          setErrors(errors);
          setValidFields((prev) => ({ ...prev, number: false }));
          return errors;
        }

        setGatewayPayload({ nonce: verifyResult.nonce });
        resetErrors();
        return null;
      } catch (error) {
        if (isBraintreeError(error)) {
          switch (error.code) {
            case "HOSTED_FIELDS_FIELDS_EMPTY": {
              const errors = {
                number: t("errors.card-number") as string,
                expirationDate: t("errors.expiration-date") as string,
                cvv: t("errors.cvv") as string,
              };
              setErrors(errors);
              setValidFields({
                number: false,
                expirationDate: false,
                cvv: false,
              });
              return errors;
            }

            case "HOSTED_FIELDS_FIELDS_INVALID": {
              const fields = error.details.invalidFieldKeys as PaymentErrors[];
              const fieldErrors = invalidFieldErrors(fields, errorMessages);

              setValidFields((prev) => {
                const updated = { ...prev };
                fields.forEach((field) => {
                  updated[field] = false;
                });
                return updated;
              });
              setErrors(fieldErrors);
              return fieldErrors;
            }

            case "HOSTED_FIELDS_TOKENIZATION_NETWORK_ERROR": {
              const errors = {
                number: t("errors.network-error") as string,
              };
              setErrors(errors);
              setValidFields((prev) => ({ ...prev, number: false }));
              return errors;
            }

            // 3DS could not judge the card, so "try another card" would send
            // the donor away from a card that may well be fine.
            case "THREEDS_LOOKUP_ERROR":
            case "THREEDS_CARDINAL_SDK_RESPONSE_TIMEDOUT":
            case "THREEDS_CARDINAL_SDK_ERROR": {
              trackError(error as Error, {
                level: THREEDS_FAILURE_LEVELS[error.code],
              });

              const errors = {
                number: t("errors.network-error") as string,
              };
              setErrors(errors);
              setValidFields((prev) => ({ ...prev, number: false }));
              return errors;
            }

            // Braintree saw a concurrent tokenization the guard above missed
            // (e.g. one started by a previous instance). Stop the attempt
            // without a message rather than blaming the donor's card.
            case "HOSTED_FIELDS_TOKENIZATION_ALREADY_IN_PROGRESS": {
              return {};
            }

            default: {
              trackUnlessCustomerError(error);

              const errors = {
                number: t("errors.invalid-card") as string,
              };
              setErrors(errors);
              setValidFields((prev) => ({ ...prev, number: false }));
              return errors;
            }
          }
        } else {
          const errors = {
            number: t("errors.card-number") as string,
          };
          setValidFields((prev) => ({ ...prev, number: false }));
          setErrors(errors);
          return errors;
        }
      } finally {
        isTokenizingRef.current = false;
      }
    },
    [
      enabled,
      setErrors,
      setGatewayPayload,
      resetErrors,
      t,
      setIsValidated,
      errorMessages,
      amount,
      threeDSecure,
      address,
    ],
  );

  const validateField = useCallback(
    (event: HostedFieldsEvent): string | null => {
      if (!enabled) {
        return null;
      }

      const { isValid, isEmpty } = event.fields[event.emittedBy];
      const field = event.emittedBy as PaymentErrors;

      const message = !isValid && !isEmpty ? errorMessages[field] : null;
      setValidFields((prev) => ({
        ...prev,
        [field]: isValid,
      }));
      setErrors({ [field]: message });
      return message;
    },
    [enabled, setErrors, errorMessages],
  );

  return {
    validateField,
    validFields,
    validate,
  };
}
